import { useMemo } from 'react'

import { useParams } from 'react-router-dom'

import {
  getArtistBalance,
  getCountryName,
} from 'src/Redux/auth-process/userSlice/selectors'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { getTiktokPrice } from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { FIXED_BY } from 'src/Constants/constants'
import { spotifyTotalPriceSelector } from 'src/Redux/spotifyCampaignMatchingProcess'

import { useGetMetaAdsCampaignByIdQuery } from '../Redux/adsCampaignApi'

import { usePayment } from './usePayment'
import { useAppSelector } from './redux'
import { useCurrency } from './useCurrency'

interface UsePaymentAmountsWithCurrency {
  currencyDiscount: string
  campaignCurrencyBudget: string
  currencyVat: string
  currencyBalance: string
  currencyTotal: string
  isVATCountry: boolean
  hasBalance: boolean
  dollarTotal?: number
}

const calculateCurrencyValue = (
  amount: number | undefined,
  currencyRate: number | undefined,
  currencySymbol?: string,
  isDiscountValue?: boolean,
): string => {
  const prefixSign = isDiscountValue ? '- ' : ''

  if (!amount || !currencyRate) {
    return `${currencySymbol}0.00`
  }
  return `${prefixSign}${currencySymbol}${(amount * currencyRate).toFixed(FIXED_BY)}`
}
export const FALLBACK_CURRENCY = {
  currencyRate: 1,
  currencySymbol: '$',
}

export const usePaymentAmountsWithCurrency = (
  platformType: CampaignPlatformTypeEnum,
): UsePaymentAmountsWithCurrency => {
  const { number } = useParams()
  const { calculatePaymentAmounts } = usePayment()
  const { data: metaAdsCampaign } = useGetMetaAdsCampaignByIdQuery(
    Number(number),
  )

  const { currency: currentCurrency } = useCurrency(platformType)

  const { currencyRate, currencySymbol } = useMemo(
    () => ({
      currencyRate:
        currentCurrency?.currencyRate || FALLBACK_CURRENCY.currencyRate,
      currencySymbol:
        currentCurrency?.currencySymbol || FALLBACK_CURRENCY.currencySymbol,
    }),
    [currentCurrency],
  )

  const spotifyCampaignPrice = useAppSelector(spotifyTotalPriceSelector) || 0
  const tikTokCampaignPrice = useAppSelector(getTiktokPrice) || 0

  const getPrice = (): number => {
    if (platformType === CampaignPlatformTypeEnum.SPOTIFY) {
      return spotifyCampaignPrice || 0
    }
    if (platformType === CampaignPlatformTypeEnum.META) {
      return metaAdsCampaign?.totalBudget || 0
    }
    return tikTokCampaignPrice || 0
  }

  const price = getPrice()

  const country = useAppSelector(getCountryName) || 'US'
  const balance = useAppSelector(getArtistBalance)

  const currentPaymentAmounts = calculatePaymentAmounts({
    balance: platformType === CampaignPlatformTypeEnum.META ? 0 : balance,
    coupon: null,
    price,
    country,
  }) || {
    balanceUseAmount: 0,
    VATAmount: 0,
    couponDiscountAmount: 0,
    total: 0,
  }

  const isVATCountry = Boolean(currentPaymentAmounts.VATAmount)

  const currencyDiscount = calculateCurrencyValue(
    currentPaymentAmounts.couponDiscountAmount,
    currencyRate,
    currencySymbol,
    true,
  )

  const campaignCurrencyBudget = calculateCurrencyValue(
    price,
    currencyRate,
    currencySymbol,
  )

  const currencyBalance = calculateCurrencyValue(
    currentPaymentAmounts.balanceUseAmount,
    currencyRate,
    currencySymbol,
  )

  const currencyVat = calculateCurrencyValue(
    currentPaymentAmounts.VATAmount,
    currencyRate,
    currencySymbol,
  )

  const currencyTotal = calculateCurrencyValue(
    currentPaymentAmounts.total,
    currencyRate,
    currencySymbol,
  )

  return {
    currencyDiscount,
    campaignCurrencyBudget,
    currencyVat,
    currencyBalance,
    currencyTotal,
    isVATCountry,
    hasBalance: Boolean(balance),
    dollarTotal: currentPaymentAmounts.total,
  }
}
